import { useMemo } from 'react';

import * as Styled from './teaching-learning-sequence.style';
import {
  getActivityMaterials,
  getNotes,
  getQuestionAnswer,
  getQuestionLevelOfAttainment,
  getRichtextContent,
  hasSequenceContent,
  sortTheEs,
} from './teaching-learning-sequence.utils';
import {
  LEARNING_SEQUENCE_QUESTION_CATEGORIES,
  LEARNING_SEQUENCE_ITEM_TYPE_QUESTION,
  LEARNING_SEQUENCE_CATEGORY_EXPLANATION,
} from 'global/constants';
import { LessonTeachingLearningSequenceFragment_teachingAndLearningSequence } from 'approot/lesson/sections/teaching-learning-sequence/__generated__/LessonTeachingLearningSequenceFragment';
import TeachingLearningSequenceNotesView from './teaching-learning-sequence-notes-view';
import { getQuestionNumberWithSpans } from 'approot/shared/teaching-learning-sequence/teaching-learning-sequence.utils';
import { spacing4 } from 'approot/shared/styles/spacing.style';
import { TeachingLearningSequenceRichtextView } from './teaching-learning-sequence-richtext';
import { Differentiation } from 'approot/shared/differentiation/differentiation.constants';
import DifferentiationShape from 'approot/shared/differentiation/differentiation-shape';
import { fontSize18, Heading18 } from 'approot/shared/styles/typography.style';
import {
  TeachingLearningSequenceEnrichmentModal,
  TeachingLearningSequenceCheckpointModal,
} from './teaching-learning-sequence-modal';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { TeachingAndLearningSequence } from './teaching-learning-sequence.constants';
import { useRegionData } from 'approot/shared/region/region.apollo';
import { getDifferentiationHeading } from 'approot/shared/differentiation/differentiation.utils';
import { isDifferentiatedAssessmentActivity } from 'approot/shared/logic/is-differentiated-assessment-activity';
import TooltipInfo from 'approot/shared/tooltip/tooltip-info';
import { getDifferentiationDescription } from 'approot/activity-builder/components/activity/activity-view-details.utils';
import TeachingLearningSequenceCheckpointButtons from './teaching-learning-sequence-buttons';
import { getRegionalisedLink } from 'approot/shared/logic/get-regionalised-link';
import { getDifferentiationBlogLink } from '../logic/get-differentiation-blog-link';
import LessonMaterialsList from '../lesson-materials/lesson-materials-list';
import { GetLessonTeachingAndLearningSequence_getLessonTeachingAndLearningSequence } from 'approot/activity-builder/components/teaching-notes/__generated__/GetLessonTeachingAndLearningSequence';
import TeachingLearningMetaData from './teaching-learning-meta-data';

type ViewProps = {
  sequences: TeachingAndLearningSequence[] | null;
  isSmall?: boolean;
};

const TeachingLearningSequenceQuestionsView = (props: ViewProps) => {
  const { sequences } = props;

  const explanations = useMemo(() => {
    return (
      sequences?.filter(
        s => s.category === LEARNING_SEQUENCE_CATEGORY_EXPLANATION
      ) || []
    );
  }, [sequences]);

  return (
    <Styled.TeachingLearningSequenceQuestionsWrapper>
      {explanations?.map((explanation, index) => {
        return (
          <TeachingLearningSequenceQuestionView
            index={index}
            questions={explanations}
            key={explanation.id}
            {...props}
          />
        );
      })}
    </Styled.TeachingLearningSequenceQuestionsWrapper>
  );
};

export default TeachingLearningSequenceQuestionsView;

type QuestionViewProps = {
  index: number;
  questions: TeachingAndLearningSequence[];
} & ViewProps;

const TeachingLearningSequenceQuestionView = ({
  index,
  questions,
  isSmall,
  sequences,
}: QuestionViewProps) => {
  const { isRegionAU } = useRegionData();

  const item = questions[index];

  const range = useMemo(() => {
    return getQuestionNumberWithSpans(
      questions.map(q => {
        return { span: q.data.span };
      }),
      index
    );
  }, [questions, index]);

  const number = range ? range.start : undefined;
  const endingNumber = range
    ? range.start === range.end
      ? undefined
      : range.end
    : undefined;
  const questionGroupId = typeof item.id === 'number' ? item.id : undefined;

  const notes = useMemo(() => {
    return getNotes({
      itemType: LEARNING_SEQUENCE_ITEM_TYPE_QUESTION,
      sequences,
      questionGroupId,
    });
  }, [sequences, questionGroupId]);

  const answer = useMemo(() => {
    return getQuestionAnswer(sequences, questionGroupId);
  }, [sequences, questionGroupId]);

  const levelOfAttainment = useMemo(() => {
    return getQuestionLevelOfAttainment(sequences, questionGroupId);
  }, [sequences, questionGroupId]);

  const activityMaterials = useMemo(() => {
    return getActivityMaterials(
      sequences as GetLessonTeachingAndLearningSequence_getLessonTeachingAndLearningSequence[],
      undefined
    );
  }, [sequences]);

  const isCheckPoint = !!item.data.checkPoint;
  const isFurtherEnrichment = !!item.data.furtherEnrichment;
  const hasEs =
    'theEsDetails' in item && item.theEsDetails && item.theEsDetails.length > 0;

  const differentiation =
    item.data.differentiation || Differentiation.GreenCircle;
  const duration = item.data.duration;

  const {
    isVisible: isEnrichmentModalVisible,
    openModal: openEnrichmentModal,
    closeModal: closeEnrichmentModal,
  } = useSimpleModalState();
  const {
    isVisible: isCheckpointModalVisible,
    openModal: openCheckpointModal,
    closeModal: closeCheckpointModal,
  } = useSimpleModalState();

  if (
    !hasSequenceContent([
      item,
      ...(notes || []),
      ...(answer ? [answer] : []),
      ...(levelOfAttainment ? [levelOfAttainment] : []),
    ])
  )
    return null;

  return (
    <Styled.TeachingLearningSequenceViewContainer>
      <Styled.TeachingLearningSequenceViewHeader>
        <DifferentiationShape
          differentiation={differentiation}
          number={number}
          width={32}
          height={32}
          fontSize={fontSize18}
        />

        {endingNumber && (
          <>
            <Styled.TeachingLearningSequenceArrow
              id="icon_arrow_left"
              width={20}
              height={20}
            />

            <DifferentiationShape
              differentiation={differentiation}
              number={endingNumber}
              width={32}
              height={32}
              fontSize={fontSize18}
            />
          </>
        )}

        <Styled.TeachingLearningSequenceQuestionDetails>
          <div>
            <Heading18 as="h3" marginBottom={spacing4}>
              <span>{getDifferentiationHeading(differentiation)} Activity</span>
              <TooltipInfo
                hoverText={getDifferentiationDescription(differentiation)}
                multiline
              />
            </Heading18>
          </div>
          <div>
            {!!duration && (
              <Styled.TeachingLearningSequenceQuestionDuration>
                {duration} mins{' '}
                {!isDifferentiatedAssessmentActivity(differentiation) && '•'}
              </Styled.TeachingLearningSequenceQuestionDuration>
            )}

            {!isDifferentiatedAssessmentActivity(differentiation) && (
              <Styled.TeachingLearningSequenceLink
                href={getRegionalisedLink(
                  getDifferentiationBlogLink({ isRegionAU })
                )}
                aria-label="Learn about Differentiation with Inquisitive (opens in a new tab)"
                target="_blank"
              >
                Learn about Differentiation with Inquisitive
              </Styled.TeachingLearningSequenceLink>
            )}
          </div>
        </Styled.TeachingLearningSequenceQuestionDetails>
      </Styled.TeachingLearningSequenceViewHeader>

      <TeachingLearningSequenceCheckpointButtons
        isCheckPoint={isCheckPoint}
        isFurtherEnrichment={isFurtherEnrichment}
        onClickCheckPoint={openCheckpointModal}
        onClickFurtherEnrichment={openEnrichmentModal}
      />

      {isEnrichmentModalVisible && (
        <TeachingLearningSequenceEnrichmentModal close={closeEnrichmentModal} />
      )}
      {isCheckpointModalVisible && (
        <TeachingLearningSequenceCheckpointModal close={closeCheckpointModal} />
      )}

      <Styled.TeachingLearningSequenceViewContent>
        <TeachingLearningSequenceRichtextView
          content={getRichtextContent(item.data)}
          isSmall={isSmall}
        />

        <TeachingLearningSequenceRichtextView
          title="Answer"
          content={getRichtextContent(answer?.data)}
          isSmall={isSmall}
        />

        {activityMaterials && 'lessonMaterials' in activityMaterials && (
          <>
            <Styled.TeachingLearningSequenceViewTitle marginBottom={8}>
              Activity Materials
            </Styled.TeachingLearningSequenceViewTitle>
            <LessonMaterialsList list={activityMaterials.lessonMaterials} />
          </>
        )}

        {isCheckPoint && (
          <TeachingLearningSequenceRichtextView
            title="Level of student attainment &amp; observations of student performance"
            content={getRichtextContent(levelOfAttainment?.data)}
            isSmall={isSmall}
          />
        )}

        <TeachingLearningSequenceNotesView
          notes={notes}
          isSmall={isSmall}
          categories={LEARNING_SEQUENCE_QUESTION_CATEGORIES}
        />

        {hasEs && (
          <TeachingLearningMetaData
            title="5e Pedagogical Model"
            items={sortTheEs(
              (item as LessonTeachingLearningSequenceFragment_teachingAndLearningSequence)
                .theEsDetails
            )}
          />
        )}
      </Styled.TeachingLearningSequenceViewContent>
    </Styled.TeachingLearningSequenceViewContainer>
  );
};
